import { type ReactElement } from "react";
import { router } from "@inertiajs/react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select";
import { useTranslations } from "@/hooks/use-translations";
import { cn } from "@/lib/utils";

interface Language {
    value: string;
    label: string;
    flag: string;
}

const languages: Language[] = [
    { value: "en", label: "English", flag: "🇬🇧" },
    { value: "da", label: "Dansk", flag: "🇩🇰" },
    { value: "de", label: "Deutsch", flag: "🇩🇪" },
];

interface LanguageSelectorProps {
    variant?: "light" | "dark";
    className?: string;
}

function setLocaleCookie(locale: string): void {
    document.cookie = `locale=${locale};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
}

export function LanguageSelector({
    variant = "light",
    className,
}: LanguageSelectorProps): ReactElement {
    const { locale } = useTranslations();

    function handleChange(value: string): void {
        setLocaleCookie(value);
        router.reload();
    }

    const isDark = variant === "dark";

    return (
        <Select value={locale} onValueChange={handleChange}>
            <SelectTrigger
                className={cn(
                    "h-8 w-auto gap-2 border-none bg-transparent px-2 text-xs font-medium shadow-none focus:ring-0",
                    isDark
                        ? "text-white/70 hover:text-white"
                        : "text-muted-foreground hover:text-foreground",
                    className,
                )}
            >
                <SelectValue />
            </SelectTrigger>
            <SelectContent align="end" className="min-w-[140px]">
                {languages.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value} className="text-sm">
                        <span className="inline-flex items-center gap-2">
                            <span className="text-base leading-none">{lang.flag}</span>
                            {lang.label}
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
